import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Save, Shield, KeyRound, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axiosInstance from '../../api/axios';
import useAuthStore from '../../store/authStore';
import { toast } from 'sonner';

const AdminProfile = () => {
  const { user, setAuth, token } = useAuthStore();

  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });

  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/auth/me');
        setProfile({
          first_name: res.data.first_name || '',
          last_name: res.data.last_name || '',
          email: res.data.email || '',
        });
      } catch (e) {
        toast.error('Fehler beim Laden des Profils');
      }
    })();
  }, []);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await axiosInstance.put('/auth/profile', {
        first_name: profile.first_name,
        last_name: profile.last_name,
      });
      // Update store so navbar etc. reflect new name
      try {
        setAuth({ ...user, ...res.data }, token);
      } catch (_) {}
      toast.success('Profil erfolgreich aktualisiert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwords.new_password.length < 8) {
      toast.error('Neues Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('Die neuen Passwörter stimmen nicht überein');
      return;
    }
    if (passwords.current_password === passwords.new_password) {
      toast.error('Das neue Passwort muss sich vom aktuellen unterscheiden');
      return;
    }

    setSavingPassword(true);
    try {
      await axiosInstance.put('/auth/change-password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      toast.success('Passwort erfolgreich geändert');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Passwort-Änderung fehlgeschlagen');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#F5EDE4' }} data-testid="admin-profile-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-[#E8DCC8]">
            <User className="h-6 w-6 text-[#5D4E3C]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#5D4E3C]">Mein Profil</h1>
            <p className="text-sm text-[#8B7355]">Verwalten Sie Ihre persönlichen Daten und Ihr Passwort</p>
          </div>
        </div>

        {/* Profil-Daten Card */}
        <Card className="rounded-none border-[#D4C4B0]">
          <CardHeader className="border-b border-[#E8DCC8]">
            <CardTitle className="text-[#5D4E3C] flex items-center gap-2">
              <Shield className="h-5 w-5" /> Persönliche Daten
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="text-[#5D4E3C]">Vorname</Label>
                  <Input
                    id="first_name"
                    type="text"
                    value={profile.first_name}
                    onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                    className="rounded-none border-[#D4C4B0]"
                    data-testid="profile-first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-[#5D4E3C]">Nachname</Label>
                  <Input
                    id="last_name"
                    type="text"
                    value={profile.last_name}
                    onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                    className="rounded-none border-[#D4C4B0]"
                    data-testid="profile-last-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-[#5D4E3C] flex items-center gap-2">
                  <Mail className="h-4 w-4" /> E-Mail-Adresse
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  disabled
                  className="rounded-none border-[#D4C4B0] bg-gray-50 text-gray-600"
                  data-testid="profile-email-input"
                />
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  E-Mail-Änderung aus Sicherheitsgründen nicht möglich. Bei Bedarf neuen Admin anlegen.
                </p>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={savingProfile}
                  className="rounded-none bg-[#E8DCC8] text-[#5D4E3C] hover:bg-[#D4C4B0]"
                  data-testid="profile-save-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingProfile ? 'Speichern…' : 'Profil speichern'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Passwort ändern Card */}
        <Card className="rounded-none border-[#D4C4B0]">
          <CardHeader className="border-b border-[#E8DCC8]">
            <CardTitle className="text-[#5D4E3C] flex items-center gap-2">
              <Lock className="h-5 w-5" /> Passwort ändern
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="current_password" className="text-[#5D4E3C]">Aktuelles Passwort</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showCurrent ? 'text' : 'password'}
                    value={passwords.current_password}
                    onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                    required
                    className="rounded-none border-[#D4C4B0] pr-20"
                    data-testid="profile-current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[#8B7355] underline"
                    data-testid="profile-toggle-current-password"
                  >
                    {showCurrent ? 'verbergen' : 'anzeigen'}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="new_password" className="text-[#5D4E3C]">Neues Passwort</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showNew ? 'text' : 'password'}
                    value={passwords.new_password}
                    onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                    required
                    minLength={8}
                    placeholder="Mindestens 8 Zeichen"
                    className="rounded-none border-[#D4C4B0] pr-20"
                    data-testid="profile-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[#8B7355] underline"
                    data-testid="profile-toggle-new-password"
                  >
                    {showNew ? 'verbergen' : 'anzeigen'}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm_password" className="text-[#5D4E3C]">Neues Passwort bestätigen</Label>
                <Input
                  id="confirm_password"
                  type={showNew ? 'text' : 'password'}
                  value={passwords.confirm_password}
                  onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                  required
                  minLength={8}
                  className="rounded-none border-[#D4C4B0]"
                  data-testid="profile-confirm-password-input"
                />
              </div>

              <div className="bg-[#FFF8E1] border-l-4 border-[#F0AD4E] p-3 text-sm text-[#5C4A00] flex gap-2">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Sicherheits-Hinweis:</strong> Passwörter werden verschlüsselt gespeichert und können
                  nicht eingesehen werden – nur neu gesetzt. Notieren Sie sich Ihr Passwort sicher.
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={savingPassword}
                  className="rounded-none bg-[#E8DCC8] text-[#5D4E3C] hover:bg-[#D4C4B0]"
                  data-testid="profile-change-password-btn"
                >
                  <KeyRound className="h-4 w-4 mr-2" />
                  {savingPassword ? 'Wird geändert…' : 'Passwort ändern'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminProfile;
